// pages/module-pages/candidatures/CandidatureDetailsPage.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  FileText,
} from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import useCandidatures from "../../../hooks/features/candidatures/useCandidatures";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { MAIN_URL } from "../../../config/api";

export default function CandidatureDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { candidatures, fetchCandidatures, updateCandidature } = useCandidatures();
  const [candidature, setCandidature] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCandidature();
  }, [id, candidatures]);

  const loadCandidature = () => {
    const found = candidatures.find((c) => c.id === id);
    if (found) {
      setCandidature(found);
    } else {
      fetchCandidatures().then(() => {
        const refound = candidatures.find((c) => c.id === id);
        setCandidature(refound);
      });
    }
    setLoading(false);
  };

  const handleStatusChange = async (status: string) => {
    const success = await updateCandidature(id!, status);
    if (success) {
      alertSuccess(
        `Candidature ${status === "accepted" ? "acceptée" : status === "rejected" ? "rejetée" : "examinée"}`
      );
      loadCandidature();
    } else {
      alertError("Erreur lors de la mise à jour");
    }
  };

  const handleDownloadCV = () => {
    if (candidature?.cv_path) {
      window.open(`${MAIN_URL}/storage/${candidature.cv_path}`, "_blank");
    }
  };

  const formatDate = (date: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return { label: "En attente", className: "bg-yellow-100 text-yellow-700" };
      case "reviewed":
        return { label: "Examinée", className: "bg-blue-100 text-blue-700" };
      case "accepted":
        return { label: "Acceptée", className: "bg-green-100 text-green-700" };
      case "rejected":
        return { label: "Rejetée", className: "bg-red-100 text-red-700" };
      default:
        return { label: "En attente", className: "bg-gray-100 text-gray-700" };
    }
  };

  if (loading) {
    return (
      <PageLayout title="Chargement..." className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  if (!candidature) {
    return (
      <PageLayout title="Candidature non trouvée" className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Candidature introuvable</p>
          <button onClick={() => navigate("/admin/candidatures")} className="mt-4 text-primary hover:underline">
            Retour à la liste
          </button>
        </div>
      </PageLayout>
    );
  }

  const statusInfo = getStatusLabel(candidature.status);

  return (
    <PageLayout title="Détails de la candidature" className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-4xl mx-auto">
        {/* Bouton retour */}
        <button
          onClick={() => navigate("/admin/candidatures")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
        >
          <ArrowLeft size={20} />
          Retour à la liste
        </button>

        {/* En-tête */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{candidature.full_name}</h1>
              <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Mail size={14} />
                  {candidature.email}
                </span>
                {candidature.phone && (
                  <span className="flex items-center gap-1">
                    <Phone size={14} />
                    {candidature.phone}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
              <p className="text-xs text-gray-400 mt-2">Reçue le {formatDate(candidature.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Poste */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase size={20} className="text-primary" />
            Poste concerné
          </h2>
          <div className="border-t border-gray-100 pt-4">
            <p className="text-gray-800">{candidature.job_offer?.title || candidature.job_offer_id}</p>
          </div>
        </div>

        {/* Lettre de motivation */}
        {candidature.cover_letter && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-primary" />
              Lettre de motivation
            </h2>
            <div className="border-t border-gray-100 pt-4">
              <p className="text-gray-700 whitespace-pre-wrap">{candidature.cover_letter}</p>
            </div>
          </div>
        )}

        {/* CV */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Download size={20} className="text-primary" />
            Curriculum Vitae
          </h2>
          <div className="border-t border-gray-100 pt-4">
            <button onClick={handleDownloadCV} className="flex items-center gap-2 text-primary hover:underline">
              <Download size={16} />
              Télécharger le CV
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-primary" />
            Actions
          </h2>
          <div className="border-t border-gray-100 pt-4">
            <div className="flex flex-wrap gap-3">
              {candidature.status === "pending" && (
                <button
                  onClick={() => handleStatusChange("reviewed")}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Clock size={16} />
                  Marquer comme examinée
                </button>
              )}
              {(candidature.status === "pending" || candidature.status === "reviewed") && (
                <>
                  <button
                    onClick={() => handleStatusChange("accepted")}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle size={16} />
                    Accepter
                  </button>
                  <button
                    onClick={() => handleStatusChange("rejected")}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle size={16} />
                    Rejeter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
